import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The "Resend code in 0:29" timer.
 *
 * The duration is NOT a constant in this file — it comes from the send
 * response's `resend_in_seconds`, because the server is the thing that actually
 * enforces the cooldown. A client-side guess that is shorter produces a button
 * that looks ready and returns 429; one that is longer makes a customer wait for
 * no reason.
 *
 * The deadline is a timestamp rather than a decrementing counter: a phone that
 * sleeps for twenty seconds stops firing intervals, and a counter would resume
 * from where it paused, leaving the button disabled long after the server would
 * accept another request.
 */
export function useResendCooldown() {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const deadlineRef = useRef<number>(0);

  const tick = useCallback(() => {
    const remaining = Math.ceil((deadlineRef.current - Date.now()) / 1000);
    setSecondsLeft(remaining > 0 ? remaining : 0);
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [secondsLeft, tick]);

  const start = useCallback(
    (seconds: number) => {
      const safe = Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 0;
      deadlineRef.current = Date.now() + safe * 1000;
      setSecondsLeft(safe);
      tick();
    },
    [tick],
  );

  return { secondsLeft, start, active: secondsLeft > 0 } as const;
}

/** "0:29" — the shape people read on a timer, not "29 seconds". */
export function formatCooldown(seconds: number): string {
  const safe = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}
